import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  CardStyle,
  ChipPresets,
  getCardStyle,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/restaurantCardsService";
import { GREEN, GREEN_DARK, errMsg } from "../ThemeStudio/themeUtils";
import { CardLookTab } from "./CardLookTab";
import { CardRestaurantsTab } from "./CardRestaurantsTab";

type TabKey = "look" | "restaurants";

/** Card Studio: how restaurant cards look on the buyer home. */
export const CardStudio: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = React.useState<TabKey>("look");
  const [style, setStyle] = React.useState<CardStyle | null>(null);
  const [presets, setPresets] = React.useState<ChipPresets>({});
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [toast, setToast] = React.useState<{ msg: string; ok: boolean } | null>(null);
  const [lookDirty, setLookDirty] = React.useState(false);
  const [pendingNav, setPendingNav] = React.useState<null | (() => void)>(null);

  const showToast = React.useCallback((msg: string, ok: boolean) => setToast({ msg, ok }), []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await getCardStyle();
      setStyle(r.style);
      setPresets(r.chip_presets);
    } catch (e) {
      setLoadError(errMsg(e, "Could not load the card look. Check your connection and try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // Unsaved look changes: ask before leaving the tab or the page.
  const guard = (fn: () => void) => (lookDirty && tab === "look" ? setPendingNav(() => fn) : fn());

  return (
    <Box sx={{ p: 3, maxWidth: 1280 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => guard(() => navigate("/buyer-app-dashboard"))}
        sx={{ textTransform: "none", color: GREEN_DARK, mb: 1, ml: -1 }}
      >
        Back to Buyer App Dashboard
      </Button>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 24, mb: 0.5 }}>Card Studio</Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
          How restaurant cards look on the buyer home. Changes reach open apps within seconds.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => guard(() => setTab(v))}
        sx={{
          mb: 3,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
          "& .Mui-selected": { color: `${GREEN_DARK} !important` },
          "& .MuiTabs-indicator": { bgcolor: GREEN },
        }}
      >
        <Tab value="look" label="Look for all cards" />
        <Tab value="restaurants" label="Restaurants" />
      </Tabs>

      {loading ? (
        <Box sx={{ py: 10, textAlign: "center" }}>
          <CircularProgress sx={{ color: GREEN }} />
        </Box>
      ) : loadError || !style ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={load} sx={{ textTransform: "none" }}>
              Retry
            </Button>
          }
        >
          {loadError ?? "Could not load the card look."}
        </Alert>
      ) : tab === "look" ? (
        <CardLookTab saved={style} onSaved={setStyle} onToast={showToast} onDirtyChange={setLookDirty} />
      ) : (
        <CardRestaurantsTab globalStyle={style} chipPresets={presets} onToast={showToast} />
      )}

      <Dialog open={!!pendingNav} onClose={() => setPendingNav(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Discard changes?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>You have unsaved changes to the card look. They will be lost.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingNav(null)} sx={{ textTransform: "none", color: GREEN_DARK }}>
            Keep editing
          </Button>
          <Button
            color="error"
            onClick={() => {
              const fn = pendingNav;
              setPendingNav(null);
              setLookDirty(false);
              fn?.();
            }}
            sx={{ textTransform: "none" }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.ok ? "success" : "error"} onClose={() => setToast(null)} sx={{ width: "100%" }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default CardStudio;
